/**
 * Language Selector atom component
 * Provides language switching functionality with flag icons
 */

// #region Imports
import { FormControl, Select, MenuItem, Box, Typography, SelectChangeEvent, useTheme } from "@mui/material";
import { Language } from "@mui/icons-material";
import { useLanguage } from "@/hooks/use-language.hook";
import { useTranslation } from "react-i18next";
import { SupportedLanguage } from "@/services/i18n";
// #endregion

// #region Types
/**
 * Props for LanguageSelector atom component
 */
interface LanguageSelectorAtomProps {
    /** Size variant of the selector */
    size?: "small" | "medium";

    /** Whether to show the label */
    showLabel?: boolean;

    /** Whether to show language names or just flags */
    showNames?: boolean;

    /** Whether to use navbar styling (light text on dark background) */
    colorMode?: "default" | "navbar";

    /** Optional test ID for testing purposes */
    "data-testid"?: string;
}
// #endregion

// #region Component
/**
 * LanguageSelector atom component for switching application language
 * Displays available languages with flags and handles language switching
 *
 * @param props - The component props
 * @returns JSX element representing the language selector
 */
function LanguageSelectorAtom({
    size = "medium",
    showLabel = true,
    showNames = true,
    colorMode = "default",
    "data-testid": testId,
    ...props
}: LanguageSelectorAtomProps) {
    const { t } = useTranslation("components");
    const { currentLanguage, availableLanguages, changeLanguage, isChangingLanguage } = useLanguage();
    const theme = useTheme();

    /**
     * Handle language selection change
     */
    const handleLanguageChange = async (event: SelectChangeEvent<string>) => {
        const selectedLanguage = event.target.value as SupportedLanguage;
        try {
            await changeLanguage(selectedLanguage);
        } catch (error) {
            console.error("Failed to change language:", error);
            // You could show a toast notification here
        }
    };

    /**
     * Render language option
     */
    const renderLanguageOption = (languageCode: SupportedLanguage, isSelected = false) => {
        const lang = availableLanguages.find(l => l.code === languageCode);
        if (!lang) return null;

        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minWidth: showNames ? 120 : 40,
                }}
            >
                <Typography
                    component="span"
                    sx={{
                        fontSize: size === "small" ? "1rem" : "1.25rem",
                        lineHeight: 1,
                    }}
                    role="img"
                    aria-label={`${lang.nativeName} flag`}
                >
                    {lang.flag}
                </Typography>
                {showNames && (
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: size === "small" ? "0.875rem" : "1rem",
                            fontWeight: isSelected ? 600 : 400,
                            color:
                                colorMode === "navbar" && isSelected ? theme.palette.primary.contrastText : "inherit",
                        }}
                    >
                        {lang.nativeName}
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <FormControl size={size} disabled={isChangingLanguage} data-testid={testId} {...props}>
            {showLabel && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 0.5,
                    }}
                >
                    <Language
                        fontSize="small"
                        sx={{
                            color: colorMode === "navbar" ? theme.palette.primary.contrastText : "action.active",
                        }}
                    />
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: size === "small" ? "0.75rem" : "0.875rem",
                            color: colorMode === "navbar" ? theme.palette.primary.contrastText : "text.secondary",
                        }}
                    >
                        {t("languageSelector.label")}
                    </Typography>
                </Box>
            )}

            <Select
                id="navbar-change-language"
                value={currentLanguage}
                onChange={handleLanguageChange}
                variant="outlined"
                size={size}
                disabled={isChangingLanguage}
                renderValue={value => renderLanguageOption(value as SupportedLanguage, true)}
                sx={{
                    minWidth: showNames ? 140 : 60,
                    "& .MuiSelect-select": {
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: colorMode === "navbar" ? theme.palette.primary.contrastText : "inherit",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: colorMode === "navbar" ? `${theme.palette.primary.contrastText}30` : undefined,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: colorMode === "navbar" ? `${theme.palette.primary.contrastText}50` : undefined,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: colorMode === "navbar" ? theme.palette.primary.contrastText : undefined,
                    },
                    "& .MuiSelect-icon": {
                        color: colorMode === "navbar" ? theme.palette.primary.contrastText : "inherit",
                    },
                }}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            maxHeight: 200,
                            backgroundColor: colorMode === "navbar" ? theme.palette.primary.main : undefined,
                            "& .MuiMenuItem-root": {
                                px: 2,
                                py: 1,
                                color: colorMode === "navbar" ? theme.palette.primary.contrastText : "inherit",
                                "&:hover": {
                                    backgroundColor:
                                        colorMode === "navbar"
                                            ? `${theme.palette.primary.contrastText}15`
                                            : "action.hover",
                                },
                                "&.Mui-selected": {
                                    backgroundColor:
                                        colorMode === "navbar"
                                            ? `${theme.palette.primary.contrastText}25`
                                            : "action.selected",
                                    "&:hover": {
                                        backgroundColor:
                                            colorMode === "navbar"
                                                ? `${theme.palette.primary.contrastText}35`
                                                : "action.selected",
                                    },
                                },
                            },
                        },
                    },
                }}
                aria-label={t("languageSelector.label")}
            >
                {availableLanguages.map(language => (
                    <MenuItem key={language.code} value={language.code}>
                        {renderLanguageOption(language.code)}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}
// #endregion

// #region Exports
export default LanguageSelectorAtom;
export type { LanguageSelectorAtomProps };
// #endregion
